import { Component, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterModule } from '@angular/router';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { AuthService } from '../../core/auth/auth.service';

@Component({
  selector: 'op-forgot-password-page',
  imports: [
    RouterModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatProgressSpinnerModule,
  ],
  template: `
    <div class="auth-wrap">
      <div class="auth-card">
        <div class="auth-brand">
          <div class="auth-brand-mark">E</div>
          <span class="auth-brand-label">Eldara</span>
        </div>

        @if (!sent()) {
          <h1 class="auth-title">Reset password</h1>
          <p class="auth-sub">Enter your email — we'll send a reset link.</p>

          <form [formGroup]="form" (ngSubmit)="submit()" novalidate class="auth-form">
            <mat-form-field appearance="outline" class="full">
              <mat-label>Email</mat-label>
              <input matInput formControlName="email" type="email" autocomplete="email" />
              @if (form.controls.email.touched && form.controls.email.hasError('required')) {
                <mat-error>Email is required.</mat-error>
              }
            </mat-form-field>

            @if (auth.error(); as err) {
              <div class="auth-error">{{ err }}</div>
            }

            <button
              class="primary-blue-btn auth-submit-btn"
              type="submit"
              [disabled]="auth.loading() || form.invalid"
            >
              @if (auth.loading()) {
                <mat-progress-spinner diameter="18" mode="indeterminate"></mat-progress-spinner>
                Sending…
              } @else {
                Send reset link
              }
            </button>
          </form>
        } @else {
          <div class="sent-state">
            <div class="sent-icon">
              <mat-icon>mark_email_read</mat-icon>
            </div>
            <h1 class="auth-title">Check your inbox</h1>
            <p class="auth-sub">If that email is registered, a reset link is on its way.</p>
          </div>
        }

        <div class="auth-footer">
          <a routerLink="/auth/sign-in" class="auth-link">← Back to sign-in</a>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .auth-wrap {
      min-height: 100vh;
      display: grid;
      place-items: center;
      padding: 24px;
      background: url('/assets/panel_bg_1.png') center / cover fixed;
      background-color: #0c1a2e;
    }
    .auth-card {
      width: min(420px, 100%);
      padding: 40px 36px 32px;
      border-radius: 18px;
      background: rgba(11, 22, 38, 0.86);
      border: 1px solid rgba(71, 99, 133, 0.55);
      backdrop-filter: blur(14px);
      -webkit-backdrop-filter: blur(14px);
      --mdc-outlined-text-field-label-text-color: rgba(217, 218, 219, 0.65);
      --mdc-outlined-text-field-input-text-color: #d9dadb;
      --mdc-outlined-text-field-outline-color: rgba(71, 99, 133, 0.65);
      --mdc-outlined-text-field-hover-outline-color: rgba(108, 150, 192, 0.85);
      --mdc-outlined-text-field-focus-outline-color: #4f96dd;
      --mdc-outlined-text-field-focus-label-text-color: #60afff;
      --mdc-outlined-text-field-caret-color: #4f96dd;
      --mdc-outlined-text-field-error-focus-outline-color: #eb6b72;
      --mdc-outlined-text-field-error-label-text-color: #eb6b72;
      --mdc-outlined-text-field-error-outline-color: rgba(235, 107, 114, 0.7);
    }
    .auth-brand {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-bottom: 28px;
    }
    .auth-brand-mark {
      display: grid;
      place-items: center;
      width: 36px;
      height: 36px;
      border-radius: 10px;
      background: rgba(37, 99, 235, 0.32);
      border: 1px solid rgba(147, 197, 253, 0.18);
      color: #93c5fd;
      font-size: 16px;
      font-weight: 700;
    }
    .auth-brand-label {
      font-size: 20px;
      font-weight: 700;
      color: #ffffff;
      letter-spacing: -0.01em;
    }
    .auth-title {
      margin: 0 0 6px;
      font-size: 22px;
      font-weight: 700;
      color: var(--op-main-text-color);
      letter-spacing: -0.01em;
    }
    .auth-sub {
      margin: 0 0 28px;
      font-size: 13px;
      color: var(--op-color-grey);
    }
    .auth-form {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }
    .full { width: 100%; }
    .auth-error {
      margin: 2px 0 8px;
      padding: 10px 14px;
      border-radius: 8px;
      background: rgba(235, 107, 114, 0.12);
      border: 1px solid rgba(235, 107, 114, 0.3);
      color: #f28a90;
      font-size: 13px;
    }
    .auth-submit-btn {
      width: 100%;
      margin-top: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
    }
    .sent-state {
      text-align: center;
      padding: 8px 0 4px;
    }
    .sent-icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 56px;
      height: 56px;
      margin-bottom: 18px;
      border-radius: 50%;
      background: rgba(47, 128, 237, 0.15);
      border: 1px solid rgba(47, 128, 237, 0.25);
      color: #60afff;
      mat-icon { font-size: 28px; width: 28px; height: 28px; }
    }
    .sent-state .auth-sub { margin-bottom: 0; }
    .auth-footer {
      margin-top: 24px;
      text-align: center;
    }
    .auth-link {
      font-size: 13px;
      color: var(--op-color-grey);
      text-decoration: none;
      transition: color 120ms ease;
      &:hover { color: var(--op-main-text-color); }
    }
    .auth-card input.mat-mdc-input-element {
      color: #d9dadb;
      caret-color: #4f96dd;
    }
    .auth-card input:-webkit-autofill,
    .auth-card input:-webkit-autofill:hover,
    .auth-card input:-webkit-autofill:focus {
      -webkit-text-fill-color: #d9dadb;
      -webkit-box-shadow: 0 0 0 1000px #0d1e35 inset;
      box-shadow: 0 0 0 1000px #0d1e35 inset;
      caret-color: #4f96dd;
      transition: background-color 9999s ease-in-out 0s;
    }
  `],
})
export class ForgotPasswordPage {
  readonly auth = inject(AuthService);
  private readonly fb = inject(FormBuilder);

  readonly sent = signal(false);

  readonly form = this.fb.nonNullable.group({
    email: ['', [Validators.required, Validators.email]],
  });

  async submit(): Promise<void> {
    this.auth.clearError();

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    try {
      await this.auth.requestPasswordReset(this.form.controls.email.value);
      this.sent.set(true);
    } catch {
      // handled in AuthService
    }
  }
}
